import {
  AddItemButton,
  KeyTakeaway,
  RecommendationSection,
} from '@inmarket/inmarkit';
import { Box } from '@mui/material';
import { useState } from 'react';
import { Annotation } from '@models/schema/chartConfiguration';

export type AnnotationGroup = {
  takeaways: Annotation[];
  recommendations?: Annotation[];
};

export type InfoSectionProps = {
  takeaways: Annotation[];
  recommendations?: Annotation[];
  editable?: boolean;
  onChange?: (newAnnotations: AnnotationGroup) => void;
};

const sortAnnotations = (a: Annotation, b: Annotation) => a.order - b.order;

export const InfoSection = (props: InfoSectionProps) => {
  const [takeawaysInput, setTakeawaysInput] = useState(props.takeaways);
  const [recommendationInput, setRecommendationInput] = useState<
    string | undefined
  >(props.recommendations?.[0]?.content);
  const [showRecommendation, setShowRecommendation] = useState(
    recommendationInput !== undefined
  );

  const handleChangeNotification = (
    takeaways: Annotation[],
    recommendations: string | undefined
  ) => {
    const takeawaysWithNewOrder = takeaways.map((takeawayText, index) => ({
      content: takeawayText.content,
      order: index,
    }));

    const recommendationsMapped = recommendations
      ? [{ content: recommendations, order: 0 }]
      : [];
    props.onChange?.({
      takeaways: takeawaysWithNewOrder,
      recommendations: recommendationsMapped,
    });
  };

  const handleSaveTakeaway = (
    newValue: Annotation['content'],
    order: Annotation['order']
  ) => {
    const newTakeaways = structuredClone(takeawaysInput);
    const toReplace = newTakeaways.findIndex(
      takeaway => takeaway.order === order
    );

    newTakeaways[toReplace] = { content: newValue, order };

    setTakeawaysInput(newTakeaways);
    handleChangeNotification(newTakeaways, recommendationInput);
  };

  const handleDeleteTakeaway = (orderToRemove: Annotation['order']) => {
    const takeawaysWithDeletion = takeawaysInput.filter(
      takeaway => takeaway.order !== orderToRemove
    );
    setTakeawaysInput(takeawaysWithDeletion);
    handleChangeNotification(takeawaysWithDeletion, recommendationInput);
  };

  const addTakeaway = () => {
    const nextOrder =
      takeawaysInput.length > 0
        ? Math.max(...takeawaysInput.map(i => i.order))
        : 0;
    setTakeawaysInput(oldValues => [
      ...oldValues,
      { content: '', order: nextOrder + 1 },
    ]);
  };

  const addRecommendation = () => {
    setShowRecommendation(true);
  };

  const handleSaveRecommendation = (
    newRecommendation: Annotation['content']
  ) => {
    const recOrUndefined =
      newRecommendation.length > 0 ? newRecommendation : undefined;
    setRecommendationInput(recOrUndefined);
    handleChangeNotification(takeawaysInput, recOrUndefined);
  };

  const handleDeleteRecommendation = () => {
    setShowRecommendation(false);
    setRecommendationInput(undefined);
    handleChangeNotification(takeawaysInput, undefined);
  };

  return (
    <>
      {takeawaysInput.toSorted(sortAnnotations).map(({ content, order }) => (
        <KeyTakeaway
          takeawayMessage={content.length > 0 ? content : undefined}
          key={order}
          editable={props.editable}
          onSave={newValue => handleSaveTakeaway(newValue, order)}
          onDelete={() => handleDeleteTakeaway(order)}
          cancelButton
        />
      ))}
      {props.editable ? (
        <Box>
          <AddItemButton onClick={addTakeaway}>Add Takeaway</AddItemButton>
        </Box>
      ) : null}
      {showRecommendation ? (
        <RecommendationSection
          recommendationMessage={recommendationInput}
          onSave={handleSaveRecommendation}
          onDelete={handleDeleteRecommendation}
          editable={props.editable}
          cancelButton
        />
      ) : (
        props.editable && (
          <Box>
            <AddItemButton onClick={addRecommendation}>
              Add Recommendation
            </AddItemButton>
          </Box>
        )
      )}
    </>
  );
};
